import { CloseWarningIcon, WarningIcon } from '@/assets/icons'
import { ADS_URL, CHAT_HISTORY_URL, CHAT_URL } from '@/constant/api'
import { generateRandomAds } from '@/utils/generateRandomAds'
import axios, { type AxiosResponse } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'
import Loader from '../Loder'
import MessageList from '../MessageLists'
import AdsCard from '../MessageLists/AdsCard'
import MessageForm from './MessagForm'

interface ChatProps {
  /** name of the site; may be undefined */
  siteName?: string
  setOpenPdfModal: (open: boolean) => void
  openPdfModal: boolean
  messagesWithAds: MessagesWithAds[]
  setMessagesWithAds: React.Dispatch<React.SetStateAction<MessagesWithAds[]>>
}
export interface Messages {
  userQuery: string
  response: string
  botResponse?: string
  imageData?: string
  type: string
}
export interface Ads {
  id: number
  company_name: string
  owner_name: string
  ad_image: string
  category_id: number
  category_name: string
  redirect_url: string
  type: string
}
export interface MessagesWithAds extends Partial<Ads>, Partial<Messages> {
  timestamp?: string
  resImg?: string
  type: string
  updatedFilename?: string
  filename?: string
  pdfLink?: string
}
export interface UserData {
  name?: string
  email?: string
  userId?: string
}

interface ChatResponse {
  answer: string
  sources?: Array<unknown>
  requestId?: string
  sessionId?: string
  responseTime?: number
  imageData?: {
    imageUrl: string
  }
  pdfUpdateData?: {
    downloadUrl: string
  }
}

function getRandomTrigger() {
  const array = new Uint32Array(1)
  crypto.getRandomValues(array)
  return ((array[0] ?? 0) % 3) + 3 // Still gives 3–5
}

const Chat: React.FC<ChatProps> = ({
  siteName,
  setOpenPdfModal,
  messagesWithAds,
  setMessagesWithAds,
}) => {
  const [ads, setAds] = useState<Ads[]>([])
  const [randomAd, setRandomAd] = useState<Ads | null>(null)
  const [loading, setLoading] = useState(true)
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [userMessageCount, setUserMessageCount] = useState(0)
  const [nextAdTrigger, setNextAdTrigger] = useState(getRandomTrigger())
  const [shownAdIds, setShownAdIds] = useState<number[]>([])
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const lastWarnTimeRef = useRef<number>(0)
  const typingEffectRef = useRef<{ cancelled: boolean }>({ cancelled: false })
  const location = useLocation()
  const [isWarning, setIsWarning] = useState<boolean>(false)

  const userData: UserData =
    location.state?.userData ??
    JSON.parse(localStorage.getItem('userData') ?? '{}')

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesWithAds])

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesWithAds])

  useEffect(() => {
    if (isWarning) {
      const timer = setTimeout(() => {
        setIsWarning(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isWarning])

  // Fetch chat history and ads
  const fetchData = useCallback(async () => {
    if (!userData?.userId) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const [messageRes, adsRes] = await Promise.allSettled([
        axios.get(`${CHAT_HISTORY_URL}/${userData.userId}`),
        axios.get(`${ADS_URL}`),
      ])

      let messageData: Messages[] = []
      if (messageRes.status === 'fulfilled') {
        messageData = messageRes.value.data.history ?? []
      }
      const formattedMessages: MessagesWithAds[] = messageData.map(msg => {
        const parsedImageData = msg?.imageData
          ? JSON.parse(msg?.imageData)
          : null

        const extractPdfLink = (text: string): string | null => {
          const regex = /(https?:\/\/[^\s]+\.pdf)/
          const match = text.match(regex)
          return match ? match[0] : null
        }
        const pdfLink = msg?.botResponse && extractPdfLink(msg?.botResponse)
        const botResponse = pdfLink
          ? 'Download the estimation by clicking the button below.'
          : msg?.botResponse
            ? msg?.botResponse
            : ''
        return {
          ...msg,
          response: parsedImageData?.imageUrl ? '' : botResponse,
          pdfLink: pdfLink ?? '',
          resImg: parsedImageData?.imageUrl ?? '',
          type: 'message',
        }
      })
      setMessagesWithAds(prev => [
        prev[0]!, // keep greeting
        ...formattedMessages,
      ])

      let adsData: Ads[] = []
      if (adsRes.status === 'fulfilled') {
        adsData = adsRes.value.data?.ads ?? []
      }
      setAds(adsData?.map(ad => ({ ...ad, type: 'ads' })))
      const randomAdData = generateRandomAds(adsData)
      setRandomAd(randomAdData ?? null)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Something went wrong'
      )
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.userId])

  useEffect(() => {
    if (userData?.userId) {
      fetchData()
    } else {
      // If no valid userData, stop loading immediately
      setLoading(false)
    }
  }, [fetchData, userData?.userId])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const randomAdData = generateRandomAds(ads)
      setRandomAd(randomAdData ?? null)
    }, 15000)

    return () => clearInterval(intervalId) // cleanup on unmount
  }, [ads])

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value
    const now = Date.now()

    if (inputValue.length > 1000) {
      if (now - lastWarnTimeRef.current > 5000) {
        setIsWarning(true)
        lastWarnTimeRef.current = now
      }
      return
    }

    setInput(inputValue)
  }

  // Handle Enter key for submit
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Helper: handle image response
  const handleImageResponse = (imageUrl: string) => {
    setMessagesWithAds(prev => {
      const updated = [...prev]
      const last = updated[updated.length - 1]
      updated[updated.length - 1] = {
        ...last,
        response: '',
        resImg: imageUrl,
        timestamp: new Date().toISOString(),
        type: 'message',
      }
      return updated
    })
  }

  const handleTypingEffect = async (fullResponse: {
    answer: string
    pdfUpdateData?: { downloadUrl: string }
  }) => {
    // Reset cancellation flag
    typingEffectRef.current.cancelled = false

    // If there's a PDF link, show a custom message instead of the raw text
    const hasDownloadUrl = fullResponse?.pdfUpdateData?.downloadUrl

    const displayText = hasDownloadUrl
      ? 'Estimation updated! You can download the PDF by clicking the button below.'
      : fullResponse.answer || ''

    let typedResponse = ''
    for (const char of displayText) {
      // Check if cancelled before processing
      if (typingEffectRef.current.cancelled) {
        break
      }

      typedResponse += char
      await new Promise(res => setTimeout(res, 7))

      // Check again after delay
      if (typingEffectRef.current.cancelled) {
        break
      }

      setMessagesWithAds(prev => {
        const updated = [...prev]
        const last = updated[updated.length - 1]
        updated[updated.length - 1] = {
          ...last,
          response: typedResponse,
          timestamp: new Date().toISOString(),
          type: 'message',
          pdfLink: hasDownloadUrl ?? '', // save link for rendering button
        }
        return updated
      })
    }
  }

  // Handle message submit
  const sendQuery = async (input: string, userId: string) => {
    return axios.post(CHAT_URL, {
      query: input,
      sessionId: userId,
      userName: userData?.name,
      userId: userData?.email,
      options: {
        useEmbeddings: true,
        searchType: 'embeddings',
        topK: 5,
        minScore: 0.3,
      },
    })
  }

  const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error))
      return error.response?.data?.message ?? 'Something went wrong'
    if (error instanceof Error) return error.message
    return 'Something went wrong'
  }

  const getUserMessage = (input: string) => {
    const payload = {
      timestamp: new Date().toISOString(),
      userQuery: input,
      response: '',
      type: 'message',
    }
    return payload
  }

  const stopRequest = useCallback(async (requestId: string) => {
    if (!requestId) return

    try {
      // Build stop URL: CHAT_URL is `${API_URL}/chat`, so we need `/chat/stop/${requestId}`
      const stopUrl = `${CHAT_URL}/stop/${requestId}`
      await axios.post(stopUrl)

      typingEffectRef.current.cancelled = true
      setCurrentRequestId(null)
      setIsTyping(false)
      toast.info('Request cancelled')
    } catch {
      typingEffectRef.current.cancelled = true
      setIsTyping(false)
      setCurrentRequestId(null)
    }
  }, [])

  const renderAds = (newCount: number) => {
    if (newCount >= nextAdTrigger) {
      const availableAds = ads.filter(
        ad => !shownAdIds.includes(ad.id) && ad.category_name !== 'Premium'
      )
      if (availableAds.length > 0) {
        const array = new Uint32Array(1)
        crypto.getRandomValues(array)
        const randomValue = typeof array[0] === 'number' ? array[0] : 0
        const index = randomValue % availableAds.length // Secure random index

        const selectedAd = availableAds[index]
        if (selectedAd) {
          setMessagesWithAds(prev => [...prev, selectedAd])
          setShownAdIds(prev => [...prev, selectedAd.id])
        }
      }

      setUserMessageCount(0)
      setNextAdTrigger(getRandomTrigger())
    } else {
      setUserMessageCount(newCount)
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isTyping) return

    setIsTyping(true)
    setCurrentRequestId(null) // Reset request ID
    typingEffectRef.current.cancelled = false // Reset cancellation flag
    const userMsg: MessagesWithAds = getUserMessage(input)
    setMessagesWithAds(prev => [...prev, userMsg])
    const userInput = input
    setInput('')

    try {
      const { data }: AxiosResponse<ChatResponse> = await sendQuery(
        userInput,
        userData?.userId ?? ''
      )

      // Save requestId from response
      if (data?.requestId) {
        setCurrentRequestId(data.requestId)
      }

      const imageUrl = data?.imageData?.imageUrl
      if (imageUrl) {
        handleImageResponse(imageUrl)
      } else {
        await handleTypingEffect(data ?? { answer: '' })
      }
      const newCount = userMessageCount + 1
      renderAds(newCount)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 499) {
        // Request was cancelled
        toast.info('Request was cancelled')
      } else {
        toast.error(getErrorMessage(error))
      }
      setCurrentRequestId(null)
    } finally {
      setIsTyping(false)
      setCurrentRequestId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, isTyping, userData?.userId])

  const handleCloseWarningMessage = () => {
    setIsWarning(false)
  }

  if (loading) return <Loader />

  return (
    <>
      <div
        className={`relative flex flex-col w-full mx-auto bg-gray-100   ${
          siteName
            ? 'h-[calc(100dvh-60px)]'
            : 'md:h-[420px] h-[calc(100dvh-150px)]'
        }`}
      >
        <button
          className='full bg-black text-white mt-2 py-2 rounded-lg max-w-[120px] mb-2 ml-auto w-full block'
          onClick={() => setOpenPdfModal(true)}
        >
          ProBid
        </button>
        {randomAd && <AdsCard msg={randomAd} />}
        <div className='h-full overflow-auto mt-[5px]'>
          <MessageList
            messages={messagesWithAds}
            messagesEndRef={messagesEndRef}
            isTyping={isTyping}
          />
        </div>

        {isWarning && (
          <div className='relative bg-yellow-100 border-l-4 mb-2 border-yellow-500 text-yellow-700 p-4 rounded-md flex items-start justify-between max-w-xl mx-auto mt-4 shadow'>
            <div className='flex items-start'>
              <WarningIcon />
              <p className='text-[14px] text-black'>
                You have reached maximum character limit of 1000
              </p>
            </div>
            <button
              className='text-yellow-700 hover:text-yellow-900 ml-4'
              onClick={handleCloseWarningMessage}
            >
              <CloseWarningIcon />
            </button>
          </div>
        )}
        <MessageForm
          input={input}
          messages={messagesWithAds}
          handleChange={handleChange}
          handleKeyDown={handleKeyDown}
          handleSubmit={handleSubmit}
          isTyping={isTyping}
          userData={
            userData ?? ({ name: '', email: '', userId: '' } as UserData)
          }
          currentRequestId={currentRequestId}
          onStopRequest={stopRequest}
        />
      </div>
    </>
  )
}

export default Chat
